import { MENU_ITEM_KEYBOARD_FOCUS_CLASS } from "@/components/Menu/menuStyles";

export type RovingChordItem = {
  chordKey?: string;
  dataTestId?: string;
  onActivate?(): void;
};

export function getRovingItemElements(root: ParentNode, selector: string): HTMLElement[] {
  const candidates: HTMLElement[] = [];

  const walk = (node: ParentNode) => {
    for (const child of node.children) {
      if (child instanceof HTMLElement && child.matches(selector)) {
        candidates.push(child);
        continue;
      }

      if (child.children.length > 0) {
        walk(child);
      }
    }
  };

  walk(root);
  return candidates;
}

/** Roving targets that are painted on screen (excludes `display:none` / `hidden` layout clones). */
export function getVisibleRovingItemElements(root: ParentNode, selector: string): HTMLElement[] {
  return getRovingItemElements(root, selector).filter((element) => {
    if (!element.isConnected) return false;

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (parseFloat(style.opacity) === 0) return false;

    return element.getClientRects().length > 0;
  });
}

export function getActiveRovingItemIndex(items: HTMLElement[]): number {
  const active = document.activeElement;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item === active || item.contains(active)) return i;
    if (item.tabIndex === 0) return i;
  }

  return -1;
}

export function clearRovingItemFocus(items: HTMLElement[]) {
  for (const element of items) {
    element.tabIndex = -1;
    element.classList.remove(MENU_ITEM_KEYBOARD_FOCUS_CLASS);
  }
}

export function setRovingItemActive(item: HTMLElement, items: HTMLElement[]) {
  clearRovingItemFocus(items);
  item.tabIndex = 0;
  item.classList.add(MENU_ITEM_KEYBOARD_FOCUS_CLASS);
  item.focus();
}

export function navigateRovingItem(items: HTMLElement[], direction: "next" | "previous") {
  if (items.length === 0) return;

  const activeIndex = getActiveRovingItemIndex(items);
  const nextIndex =
    activeIndex === -1
      ? direction === "next"
        ? 0
        : items.length - 1
      : direction === "next"
        ? (activeIndex + 1) % items.length
        : (activeIndex - 1 + items.length) % items.length;

  setRovingItemActive(items[nextIndex], items);
}

export function activateRovingItemByTestId(
  items: RovingChordItem[],
  dataTestId: string,
  onComplete?: () => void
): boolean {
  for (const item of items) {
    if (item.dataTestId !== dataTestId) continue;
    if (!item.onActivate) continue;

    item.onActivate();
    onComplete?.();
    return true;
  }

  return false;
}
